import * as Sentry from '@sentry/browser';
import queryString from 'query-string';
import { useCookies } from 'react-cookie';

import {
  MediationAgreementReportsStatus,
  PostMediationAgreementReportsTerminationReasons,
  UpdateMediationAgreementReportDateType,
} from '@type-def_iqfu/MediationAgreementReportsType';

import { UserType } from '@constants_common';
import {
  ContractStatusSearchOptions,
  MediationAgreementCategorySearchOptions,
  MediationAgreementReportsSortKey,
  SortOrderOptions,
  TerminationReasonSearchOptions,
} from '@constants_iqfu';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export const useMediationAgreementReports = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const getMediationAgreementReportsList = async (
    userType: UserType,
    queryParams: {
      company_id?: string;
      offset: number;
      limit: number;
      seller_name: string;
      expiry_date?: string; //有効期限 YYYY-MM (会社ユーザー用)
      category?: Exclude<keyof typeof MediationAgreementCategorySearchOptions, ''>; //媒介種類 (運営用)
      expiry_date_from?: string; //有効期限開始日 YYYY-MM-DD (運営用)
      expiry_date_to?: string; //有効期限終了日 YYYY-MM-DD (運営用)
      sort_key?: keyof typeof MediationAgreementReportsSortKey; //ソートキー (運営用)
      sort_order?: Exclude<keyof typeof SortOrderOptions, ''>; //ソート順 (運営用)
      contract_status?: Exclude<keyof typeof ContractStatusSearchOptions, ''>; //契約状況 (運営用)
      termination_reason?: Exclude<keyof typeof TerminationReasonSearchOptions, ''>; //契約終了理由 (運営用)
    },
  ) => {
    if (queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams);

    const url = `${backendBasePath}/${userType}/mediation_agreement_reports?${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createMediationAgreementReport = async (formData: FormData) => {
    const url = `${backendBasePath}/company/mediation_agreement_reports`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateMediationAgreementReport = async (mediation_agreement_reports_id: string, formData: FormData) => {
    const url = `${backendBasePath}/company/mediation_agreement_reports/${mediation_agreement_reports_id}`;
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getMediationAgreementReportDetail = async (mediation_agreement_report_id: string, userType: UserType) => {
    const url = `${backendBasePath}/${userType}/mediation_agreement_reports/${mediation_agreement_report_id}`;
    let status;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, result };
  };

  const editMediationAgreementReport = async (
    mediation_agreement_reports: string,
    submitData: UpdateMediationAgreementReportDateType,
  ) => {
    const url = `${backendBasePath}/admin/mediation_agreement_reports/${mediation_agreement_reports}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteMediationAgreementReport = async (mediation_agreement_report_id: string) => {
    const url = `${backendBasePath}/admin/mediation_agreement_reports/${mediation_agreement_report_id}`;
    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const downloadMediationAgreementReportsFile = async (
    userType: UserType,
    mediation_agreement_report_file_id: number,
    file_name: string,
  ) => {
    const url = `${backendBasePath}/${userType}/mediation_agreement_report_files/${mediation_agreement_report_file_id}/download`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.download = file_name;
        a.href = url;
        a.click();
        a.remove();
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 400);
      })
      .catch((error) => {
        console.error(error);
      });

    return result;
  };

  const previewMediationAgreementReportFile = async (mediation_agreement_report_file_id: number) => {
    const url = `${backendBasePath}/admin/mediation_agreement_report_files/${mediation_agreement_report_file_id}/download`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.blob();
      })
      .then((blob) => {
        return URL.createObjectURL(blob);
      })
      .catch((error) => {
        console.error(error);
      });
    return result;
  };

  const terminateMediationAgreementReport = async (
    mediation_agreement_report_id: number,
    termination_reason: keyof typeof PostMediationAgreementReportsTerminationReasons,
  ) => {
    const url = `${backendBasePath}/company/mediation_agreement_reports/${mediation_agreement_report_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ termination_reason }),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const sendUpdateConfirmationChat = async (mediation_agreement_report_id: number) => {
    const url = `${backendBasePath}/admin/mediation_agreement_reports/${mediation_agreement_report_id}/send_update_confirmation_chat`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateMediationAgreementReportStatus = async (
    id: string,
    status: keyof typeof MediationAgreementReportsStatus,
  ) => {
    const url = `${backendBasePath}/admin/mediation_agreement_reports/${id}/update_status`;
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  return {
    getMediationAgreementReportsList,
    createMediationAgreementReport,
    updateMediationAgreementReport,
    getMediationAgreementReportDetail,
    editMediationAgreementReport,
    deleteMediationAgreementReport,
    downloadMediationAgreementReportsFile,
    previewMediationAgreementReportFile,
    terminateMediationAgreementReport,
    sendUpdateConfirmationChat,
    updateMediationAgreementReportStatus,
  };
};
